import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { json, Link } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { format } from 'date-fns'
import './transaction-ledger-edit-merchant.css'

// COMPONENTS
import * as Icons from '@icons'
import { Separator } from '@components/shadcn/separator'
import Avatar from '@components/molecules/avatar'
import Button from '@components/atoms/button'
import CurrencyTextField from '@components/molecules/currency-textfield'
import MerchantLogo from '@components/atoms/merchant-logo'
import Spinner from '@components/atoms/spinner'
import TabGroup from '@components/molecules/tab-group'

// UTILITIES
import { toast } from 'sonner'
import { createResponseObj, VirtualCardAPI } from '@apis'
import { parseUserLimits } from '@schemas'
import { toUSD, useCurrencyInput } from '@hooks/inputs'
import { priceFormatter, toPennies } from '@utils/number-helper'
import { min } from '@constants'
import { isNonEmptyString } from '@utils/type-checkers'
import { flatten } from '@utils/string-helper'
import { toggleBodyScroll } from '@utils/temp-helpers'
import { queryClient } from '@/lib/queryClient'


interface LedgerSlot extends BreakdownItem {
    isManuallyUpdated: boolean
}
interface Props extends React.HTMLAttributes<HTMLDivElement> {
    isOrganizer: boolean
    canSplit: boolean
    ledgerTransactionId: string
    group: Group
    groupStatus: "active" | "completed" | "canceled" | "finalized" | "pending"
    onDrawerClose: () => void
    onGroupClose: () => void
    handleShare: () => void
    isDemo?: boolean
}

const TransactionLedgerEditMerchant: FC<Props> = (props) => {
    const { isOrganizer, canSplit, ledgerTransactionId, group, groupStatus, onDrawerClose, onGroupClose, handleShare, isDemo, ...divProps } = props
    const { user } = useUser()
    const [submittingLedger, setSubmittingLedger] = useState(false)
    const [manualEdit, setManualEdit] = useState(false)
    const [activeTab, setActiveTab] = useState('Weighted')
    const [ledgerSlots, setLedgerSlots] = useState<LedgerSlot[]>([])
    const [overMaxUserIds, setOverMaxUserIds] = useState<string[]>([])
    const originalSlotsRef = useRef<LedgerSlot[] | null>(null)

    const lineItem = useMemo(() => { return group.ledger?.find((item) => item.transactionId === ledgerTransactionId) }, [ledgerTransactionId])
    const sortedBreakdown = useMemo(() => {
        if (!!lineItem?.breakdown) {
            const arr = [...lineItem.breakdown]
            const userIndex = arr.findIndex(item => item.userId === user?.id)
            if (userIndex > -1) {
                const [userItem] = arr.splice(userIndex, 1);
                arr.unshift(userItem);
            }
            return arr as LedgerSlot[]
        }
        return [] as LedgerSlot[]
    }, [lineItem])
    const relatedTransactions = useMemo(() => { if (!!group.transactions) return group.transactions[ledgerTransactionId] }, [ledgerTransactionId, group.transactions])
    const { merchantName, merchantCategory } = useMemo(() => {
        const merchantName = relatedTransactions?.find(transaction => !!transaction.merchant.name)?.merchant.name.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()) || ''
        const merchantCategory = relatedTransactions?.find(transaction => !!transaction.mcc.category)?.mcc.category || 'unknown'
        return { merchantName, merchantCategory }
    }, [relatedTransactions])
    const spendAmount = useMemo(() => {
        let amount = 0

        const latestTransaction = relatedTransactions?.at(-1)
        if (latestTransaction && (latestTransaction.status === 'pending' || latestTransaction.status === 'finalized' || latestTransaction.status === 'Credit')) {
            if (latestTransaction.status === 'Credit') {
                amount = (relatedTransactions?.find(transaction => transaction.status === 'finalized')?.amount || 0) - latestTransaction.amount

                if (amount < 0) {
                    amount = 0
                }
            } else {
                amount = latestTransaction.amount
            }
        }

        return amount
    }, [relatedTransactions])
    const pendingTransactionAmount = useMemo(() => (relatedTransactions?.find((t) => t.status === 'pending')?.amount || 0), [relatedTransactions])

    const submittable = useMemo(() => {
        const singleTransaction = (group.ledger?.length || 0) === 1
        const slotsTotal = ledgerSlots.reduce((prev, current) => { return prev + current.amount }, 0)

        if ((ledgerSlots.every((slot) => {
            const availableFunds = (group.pledges?.find((pledge) => pledge.id === slot.id)?.availableAmount || 0) + (lineItem?.breakdown.find((item) => item.id === slot.id)?.amount || 0)
            const slotPledge = group.pledges?.find((pledge) => pledge.id === slot.id)?.amount || 0
            const slotParticipantLedgerSum = group.ledger?.reduce((prev, current) => {
                const slotPledges = current.breakdown
                    .filter(b => b.id === slot.id)
                    .reduce((innerSum, b) => innerSum + (b.amount || 0), 0);
                return prev + slotPledges
            }, 0)

            if (
                slot.amount >= (
                    singleTransaction && slotPledge === 100
                        ? 0
                        : (singleTransaction
                            ? ((slot.amount > 0 && slot.amount < 1) ? min : 0)
                            : ((slotParticipantLedgerSum || 0) - slot.amount <= 0 ? 1 : 0)
                        )
                ) &&
                slot.amount <= (
                    singleTransaction && slotPledge === 100
                        ? 100
                        : Math.min(availableFunds, spendAmount || 0)
                )
            ) return true
            else return false
        }))
            && (spendAmount === slotsTotal)
        ) return true
        else return false
    }, [ledgerSlots])

    useEffect(() => {
        const slots = [...sortedBreakdown].map(slot => ({ ...slot }))
        setLedgerSlots(slots)
        originalSlotsRef.current = slots.map(slot => ({ ...slot }))

        const activeSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        const activeAmounts = activeSlots.map(s => s.amount)
        if (activeAmounts.length > 0) {
            const minAmount = Math.min(...activeAmounts)
            const maxAmount = Math.max(...activeAmounts)
            if ((maxAmount - minAmount) <= 1) {
                handleTabChange('Equal')
                return
            }
        }

        const weighted = buildWeightedSplitSlots(slots)
        const matchesWeighted = slots.every((s) => {
            const w = weighted.find(ws => ws.id === s.id)
            return w && Math.abs(s.amount - w.amount) <= 1
        })

        if (!matchesWeighted) {
            handleTabChange('Custom')
            return
        }

        handleTabChange('Weighted')
    }, [ledgerTransactionId, sortedBreakdown])

    function updatePledgeSlot(index: number, amount: number) {
        setManualEdit(true)

        setLedgerSlots(prevSlots => {
            const arr = [...prevSlots]
            arr[index].isManuallyUpdated = true

            const isClosed = (slotId: string) => {
                const pledge = group.pledges?.find(p => p.id === slotId)
                return pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z'
            }

            const otherUnchangedSlots = arr.filter((slot, _index) => _index !== index && !slot.isManuallyUpdated && !isClosed(slot.id))
            const otherChangedSlots = arr.filter((slot, _index) => _index !== index && (!!slot.isManuallyUpdated || isClosed(slot.id)))
            const remainingAmount = Math.max(0, (spendAmount || 0) - (otherChangedSlots.reduce((prev, current) => prev + current.amount, 0) + amount))
            const newAmountForOthers = otherUnchangedSlots.length > 0 ? Math.round(remainingAmount / otherUnchangedSlots.length) : 0

            const nextSlots = arr.map((slot, _index) => {
                if (_index === index) return { ...slot, amount }
                if (!slot.isManuallyUpdated && !isClosed(slot.id)) return { ...slot, amount: newAmountForOthers }
                return slot
            })

            const overIds = getOverMaxUserIds(nextSlots)
            setOverMaxUserIds(overIds)

            return nextSlots
        })
    }

    function buildEvenSplitSlots(slots: LedgerSlot[]) {
        if (!spendAmount || slots.length === 0) return slots

        const closedSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !!(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        const closedAmount = closedSlots.reduce((sum, s) => sum + s.amount, 0)
        const activeSpendAmount = Math.max(0, spendAmount - closedAmount)

        const activeSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        if (activeSlots.length === 0) return slots

        const perSlotAmount = Math.floor(activeSpendAmount / activeSlots.length)
        const remainder = activeSpendAmount - perSlotAmount * activeSlots.length

        const highestPledgeAmount = Math.max(...(group.pledges?.map(p => p.amount) || [0]))
        const highestPledgers = group.pledges?.filter(p => p.amount === highestPledgeAmount && activeSlots.some(s => s.id === p.id)) || []
        const targetPledgerId = highestPledgers.length > 0 ? highestPledgers[Math.floor(Math.random() * highestPledgers.length)].id : activeSlots[0].id

        return slots.map(slot => {
            if (!activeSlots.some(s => s.id === slot.id)) return slot
            return {
                ...slot,
                amount: perSlotAmount + (slot.id === targetPledgerId ? remainder : 0)
            }
        })
    }

    function buildWeightedSplitSlots(slots: LedgerSlot[]) {
        if (!spendAmount || slots.length === 0) return slots

        const activeSlots = slots.filter(slot => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return !(pledge?.closedAt && pledge.closedAt !== '0001-01-01T00:00:00Z')
        })

        if (activeSlots.length === 0) return slots

        const totalPledgeAmount = slots.reduce((sum, slot) => {
            const pledge = group.pledges?.find(p => p.id === slot.id)
            return sum + (pledge?.amount || 0)
        }, 0)

        if (totalPledgeAmount === 0) return buildEvenSplitSlots(slots)

        const weightedSlots = slots.map((slot) => {
            if (!activeSlots.some(s => s.id === slot.id)) return slot

            const pledge = group.pledges?.find(p => p.id === slot.id)
            const pledgeAmount = pledge?.amount || 0
            const share = Math.floor((pledgeAmount / totalPledgeAmount) * spendAmount)
            return { ...slot, amount: share }
        })

        const sumShares = weightedSlots.reduce((sum, s) => sum + s.amount, 0)
        const remainder = spendAmount - sumShares

        if (remainder !== 0) {
            const activeTotalPledge = activeSlots.reduce((sum, s) => sum + (group.pledges?.find(p => p.id === s.id)?.amount || 0), 0)

            if (activeTotalPledge > 0) {
                // Distribute remainder proportionally
                let distributedRemainder = 0
                const adjustedSlots = weightedSlots.map(slot => {
                    if (!activeSlots.some(s => s.id === slot.id)) return slot

                    const pledge = group.pledges?.find(p => p.id === slot.id)?.amount || 0
                    const exactShare = (pledge / activeTotalPledge) * remainder
                    const intShare = Math.trunc(exactShare)
                    distributedRemainder += intShare
                    return { ...slot, amount: slot.amount + intShare }
                })

                const finalRemainder = remainder - distributedRemainder

                if (finalRemainder !== 0) {
                    const highestPledgeAmount = Math.max(...(activeSlots.map(s => group.pledges?.find(p => p.id === s.id)?.amount || 0)))
                    const highestPledgers = activeSlots.filter(s => (group.pledges?.find(p => p.id === s.id)?.amount || 0) === highestPledgeAmount)
                    const targetId = highestPledgers[0]?.id || activeSlots[0].id

                    return adjustedSlots.map(slot => {
                        if (slot.id === targetId) return { ...slot, amount: slot.amount + finalRemainder }
                        return slot
                    })
                }
                return adjustedSlots
            }
        }

        return weightedSlots
    }

    function getOverMaxUserIds(newSlots: LedgerSlot[]): string[] {
        const pledgeByUser: Record<string, number> = {}

        group.pledges?.forEach(p => {
            const uid = p.user?.id
            if (uid) pledgeByUser[uid] = p.amount
        })

        const totals: Record<string, number> = {}

        group.ledger?.forEach(item => {
            if (item.transactionId === ledgerTransactionId) return
            item.breakdown.forEach(b => {
                const uid = b.userId
                if (!uid) return
                totals[uid] = (totals[uid] || 0) + b.amount
            })
        })

        const over: string[] = []

        newSlots.forEach(slot => {
            const uid = slot.userId
            if (!uid) return

            const total = (totals[uid] || 0) + slot.amount
            const max = pledgeByUser[uid] ?? Infinity

            if (total > max) over.push(uid)
        })

        return over
    }

    function handleTabChange(tab: string) {
        setActiveTab(tab)

        let proposed = ledgerSlots
        if (tab === 'Weighted' || (originalSlotsRef.current && tab === 'Weighted')) { // Force weighted recalculation on tab switch
            proposed = buildWeightedSplitSlots(originalSlotsRef.current || ledgerSlots)
            setManualEdit(false)
        } else if (tab === 'Equal') {
            proposed = buildEvenSplitSlots(originalSlotsRef.current || ledgerSlots)
            setManualEdit(false)
        } else if (tab === 'Custom') {
            const slots = [...sortedBreakdown].map(slot => ({ ...slot }))
            proposed = slots
            setManualEdit(true)
        }

        const overIds = getOverMaxUserIds(proposed)
        setOverMaxUserIds(overIds)
        setLedgerSlots(proposed)
    }

    async function handleSubmit() {
        if (isDemo) {
            toast.info('This action is disabled in the demo')
            return
        }
        setSubmittingLedger(true)

        const groupId = group?.id
        console.log("Remove Pledge Data:", { groupId })
        if (!isNonEmptyString(groupId)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ groupId })}`
        })

        await VirtualCardAPI.UpdateGroupRequest.fetch(group.id, {
            targetAmount: group.targetAmount,
            ledger: {
                id: ledgerTransactionId,
                breakdown: ledgerSlots.map(item => ({
                    id: item.id,
                    amount: Math.round(item.amount),
                }))
            }
        }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['user-groups'] })
            onDrawerClose()

            // Check if we should call onGroupClose based on group status and transaction state
            // Don't auto-close merchant groups
            if (groupStatus === 'pending' && group.type !== 'merchant') {
                const transactionCount = group.ledger?.length || 0

                if (transactionCount === 1) {
                    // Single transaction: close the group immediately
                    onGroupClose()
                } else if (transactionCount > 1) {
                    // Multiple transactions: check if all OTHER transactions are adjusted
                    const allOtherTransactionsAdjusted = group.ledger?.every((ledgerItem) => {
                        // Skip the current transaction we just updated
                        if (ledgerItem.transactionId === ledgerTransactionId) return true

                        // Get related transactions for this ledger item
                        const relatedTxns = group.transactions?.[ledgerItem.transactionId] || []
                        const latestTxn = relatedTxns?.at(-1)
                        const isFinalized = latestTxn?.status === 'finalized' || latestTxn?.status === 'Credit'

                        if (!isFinalized) return false

                        // Calculate the expected spend amount for this transaction
                        let txnSpendAmount = 0
                        if (latestTxn) {
                            if (latestTxn.status === 'Credit') {
                                txnSpendAmount = (relatedTxns.find(t => t.status === 'finalized')?.amount || 0) - latestTxn.amount
                                if (txnSpendAmount < 0) txnSpendAmount = 0
                            } else {
                                txnSpendAmount = latestTxn.amount
                            }
                        }

                        // Check if the ledger breakdown sum matches the spend amount
                        const ledgerSum = ledgerItem.breakdown.reduce((sum, b) => sum + b.amount, 0)
                        return ledgerSum === txnSpendAmount
                    })

                    if (allOtherTransactionsAdjusted) {
                        onGroupClose()
                    }
                }
            }

            return json('success', { status: 200 })
        }).catch((error) => {
            console.error(error)
        })

        setSubmittingLedger(false)
    }

    const needsAdjustment = useMemo(() => {
        const isFinalized = relatedTransactions?.at(-1)?.status === 'finalized' || relatedTransactions?.at(-1)?.status === 'Credit'
        const ledgerSlotsSum = lineItem?.breakdown.reduce((prev, current) => prev + current.amount, 0)
        return isFinalized && ledgerSlotsSum !== spendAmount
    }, [relatedTransactions, lineItem, group, spendAmount])

    return (
        <div className='flex flex-col gap-4 transaction-ledger-edit-merchant' {...divProps}>
            {needsAdjustment && isOrganizer && (
                <div style={{
                    backgroundColor: 'var(--splitcard-red-100)',
                    borderRadius: '0.625rem',
                    padding: '1rem',
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem'
                }}>
                    <Icons.Warning width="1.5rem" height="1.5rem" tint="var(--splitcard-red-500)" style={{ flexShrink: 0 }} />
                    <p className="text-body-2" style={{ color: 'var(--splitcard-red-500)', margin: 0 }}>The final total has changed from {priceFormatter.format(pendingTransactionAmount / 100)} to {priceFormatter.format((spendAmount || 0) / 100)}. Update how the cost is split so everyone pays the correct amount.</p>
                </div>
            )}
            <div className='flex flex-col items-center gap-4'>
                <MerchantLogo merchantCategory={merchantCategory} fill={true} variant="rounded" />
                <div className='flex flex-col items-center gap-2.5'>
                    <div className='flex flex-col items-center'>
                        {!!merchantName && <h3>{merchantName}</h3>}
                        {relatedTransactions?.map((transaction, index) => {
                            const { id, transactedAt, status, amount } = transaction
                            const date = new Date(transactedAt)
                            const prev = index - 1
                            const next = index + 1
                            const isCredit = status === 'Credit'

                            return (
                                <div className='flex flex-row w-full' key={`${id}-${index}`}>
                                    <p className='flex flex-col' style={{ flex: '1' }}>
                                        <span className='text-body-2 text-gray-500'>{!!date ? format(date, "MM/dd/yy @ p") : '--/--/--'}</span>
                                    </p>
                                    <p className='text-body-2' style={{ color: (status === 'finalized' || isCredit) ? 'var(--splitcard-green-600)' : 'var(--splitcard-gray-500)', padding: '0rem 1rem', textAlign: 'right' }}>{status === 'pending' ? 'Pre-Auth' : status.charAt(0).toUpperCase() + status.slice(1)}</p>
                                    {(status === 'pending' && !!relatedTransactions[next] && (relatedTransactions[next].status === 'finalized') && (amount >= relatedTransactions[next].amount))
                                        ? <p style={{ width: '5.5rem' }}></p>
                                        : (status === 'finalized' && !!relatedTransactions[prev] && (relatedTransactions[prev].status === 'pending') && (amount > relatedTransactions[prev].amount))
                                            ? <p className='text-body' style={{ textAlign: 'right', width: '5.5rem' }}>+{priceFormatter.format((amount - relatedTransactions[prev].amount) / 100)}</p>
                                            : <p className='text-body' style={{ textAlign: 'right', width: '5.5rem' }}>{(!isCredit) ? '' : '+'}{priceFormatter.format(amount / 100)}</p>
                                    }
                                </div>
                            )
                        })}
                    </div>
                    <h1>{priceFormatter.format((spendAmount || 0) / 100)}</h1>
                </div>
            </div>

            {canSplit && (<>
                <Separator />
                <TabGroup
                    tabs={[{ name: 'Weighted' }, { name: 'Equal' }, { name: 'Custom' }]}
                    activeTab={activeTab}
                    light={true}
                    onTabChange={handleTabChange}
                />
                {activeTab === 'Weighted' && (
                    <div className="flex flex-row items-center gap-4 info-box bg-blue-100 p-4 rounded-[0.625rem] border border-blue-100">
                        <Icons.Split tint="var(--splitcard-blue-700)" width="1.5rem" height="1.5rem" />
                        <p className="text-body-2 text-blue-700 m-0">Splits are automatically calculated based on each person's contribution to the total pool.</p>
                    </div>
                )}
            </>)}

            {activeTab === 'Custom' && isOrganizer && ledgerSlots.length > 1 && (
                (() => {
                    const currentSlotsTotal = ledgerSlots.reduce((sum, slot) => sum + slot.amount, 0)
                    const targetTotal = spendAmount || 0
                    const diff = currentSlotsTotal - targetTotal
                    const isBalanced = diff === 0

                    if (diff > 0) {
                        return (
                            <div className="flex flex-row items-center justify-between info-box bg-red-100 py-3 px-4 rounded-[0.625rem] border border-red-100">
                                <p className="text-body-2 text-red-500 font-bold m-0">Amount must match the total</p>
                                <p className="text-body-2 text-red-500 font-bold m-0">Over: {priceFormatter.format(diff / 100)}</p>
                            </div>
                        )
                    }

                    if (diff < 0) {
                        return (
                            <div className="flex flex-row items-center justify-between info-box bg-red-100 py-3 px-4 rounded-[0.625rem] border border-red-100">
                                <p className="text-body-2 text-red-500 font-bold m-0">Amount must match the total</p>
                                <p className="text-body-2 text-red-500 font-bold m-0">Left: {priceFormatter.format(Math.abs(diff) / 100)}</p>
                            </div>
                        )
                    }

                    if (isBalanced) return (
                        <div className="flex flex-row items-center justify-between info-box bg-green-100 py-3 px-4 rounded-[0.625rem] border border-green-100">
                            <p className="text-body-2 text-green-700 font-bold m-0">Perfectly balanced</p>
                            <Icons.CheckSmall tint="var(--splitcard-green-700)" width="1.2rem" height="1.2rem" />
                        </div>
                    )
                    return null
                })()
            )}

            <div className='flex flex-col gap-4 items'>
                {ledgerSlots.map((slot, index) =>
                    <SlotCell
                        key={`${slot.id}-${index}`}
                        slot={slot}
                        index={index}
                        activeTab={activeTab}
                        isOrganizer={isOrganizer}
                        canSplit={canSplit}
                        spendAmount={spendAmount}
                        submittingLedger={submittingLedger}
                        group={group}
                        groupStatus={groupStatus}
                        pledges={group.pledges}
                        ledger={group.ledger}
                        ledgerSlots={ledgerSlots}
                        ledgerTransactionId={ledgerTransactionId}
                        overMaxUserIds={overMaxUserIds}
                        onPledgeUpdate={(index, amount) => updatePledgeSlot(index, amount)}
                        isDemo={isDemo}
                    />
                )}
            </div>

            {canSplit && <div className="flex flex-row items-end" style={{ flex: 1, gap: '0.625rem', display: 'grid', gridAutoFlow: 'column' }}>
                <Button theme="dark" disabled={!submittable || !!overMaxUserIds.length || submittingLedger || (!manualEdit && activeTab === 'Custom')} onClick={handleSubmit}>{submittingLedger ? <Spinner indicator='circle' style={{ margin: 'auto' }} /> : 'Save Split'}</Button>
            </div>}
        </div>
    )
}

interface Propss {
    index: number
    activeTab: string
    isOrganizer: boolean
    canSplit: boolean
    spendAmount: number | undefined
    slot: LedgerSlot
    submittingLedger: boolean
    group: Group
    groupStatus: "active" | "completed" | "canceled" | "finalized" | "pending"
    pledges: Pledge[] | undefined
    ledger: LedgerLineItem[] | undefined
    ledgerSlots: LedgerSlot[]
    ledgerTransactionId: string
    overMaxUserIds: string[]
    onPledgeUpdate: (index: number, amount: number) => void
    isDemo?: boolean
}

const SlotCell: FC<Propss> = ({ index, activeTab, isOrganizer, canSplit, group, groupStatus, pledges, ledger, ledgerSlots, ledgerTransactionId, spendAmount, slot, submittingLedger, overMaxUserIds, onPledgeUpdate, isDemo }) => {
    const { user } = useUser()
    const userLimits = useMemo(() => parseUserLimits(user?.publicMetadata?.limits), [user?.publicMetadata?.limits])
    const singleTransaction = (ledger?.length || 0) === 1
    const slotPledge = pledges?.find(pledge => pledge.id === slot.id)
    const slotPledgeAmount = slotPledge?.amount ?? 0
    const slotPledgeClosed = slotPledge?.closedAt !== '0001-01-01T00:00:00Z'
    const otherLedgerSlotsParticipantSum = useMemo(() =>
        (ledger ?? []).reduce((sum, txn) => {
            if (txn.transactionId === ledgerTransactionId) return sum

            const slotSum = txn.breakdown
                .filter(b => b.id === slot.id)
                .reduce((inner, b) => inner + (b.amount || 0), 0)

            return sum + slotSum
        }, 0),
        [ledger, ledgerTransactionId, slot.id])
    const currentAmount = slot.amount
    const proposedTotal = otherLedgerSlotsParticipantSum + currentAmount
    const availableFunds = slotPledgeAmount - proposedTotal
    const isOverMax = overMaxUserIds.includes(slot.userId || '')

    const [_, slotAmountProps, setSlotAmount] = useCurrencyInput({
        initValue: !!slot.amount ? toUSD(`${slot.amount / 100}`, undefined, undefined, true) : '0',
        min: singleTransaction ? ((slot.amount > 0 && slot.amount < 1) ? min : 0) : 0,
    })
    const slotsTotal = ledgerSlots.reduce((prev, current) => prev + current.amount, 0)

    const { userPledges } = useMemo(() => [...(group.pledges || [])].reduce<{ userPledges: Pledge[] }>((prev, pledge) => {
        if (pledge.user?.id === user?.id) return { ...prev, userPledges: [...prev.userPledges, pledge] }
        else return { ...prev }
    }, { userPledges: [] }), [user?.id, group.pledges])

    useEffect(() => {
        if (slot.isManuallyUpdated) return
        setSlotAmount(toUSD(`${slot.amount / 100}`, undefined, undefined, true))
    }, [slot.amount, slot.isManuallyUpdated])

    function handleUpdate(e: React.ChangeEvent<HTMLInputElement>) {
        const amount = e.target.value
        setSlotAmount(amount)
        onPledgeUpdate(index, toPennies(amount))
    }

    const stakePercentage = useMemo(() => {
        if (spendAmount && spendAmount > 0) {
            return Math.round((slot.amount / spendAmount) * 100)
        }
        return 0
    }, [slot.amount, spendAmount])

    return (
        <div className={flatten('pledge', isOverMax ? 'over-max' : '')}>
            <Avatar user={slot} />
            <div className="flex flex-col justify-center gap-2" style={{ flex: 2 }}>
                <p className='text-body'><strong>{slot.userId === user?.id ? `You (${slot.givenName || ''} ${slot.familyName || ''})`.trim() : `${slot.givenName || ''} ${slot.familyName || ''}`.trim()}</strong></p>
                <div className="flex flex-row items-center gap-2" style={{ marginTop: '0.2rem' }}>
                    {!slotPledgeClosed
                        ? <div style={{ backgroundColor: 'var(--splitcard-gray-100)', padding: '0.125rem 0.5rem', borderRadius: '1rem' }}>
                            <p className='text-body-2' style={{ fontSize: '0.8rem', color: 'var(--splitcard-grey-600)' }}>
                                Bal: <span style={{ color: availableFunds <= 0 ? 'var(--splitcard-red-500)' : '', fontWeight: availableFunds <= 0 ? 'bold' : '' }}>{priceFormatter.format(Math.max(availableFunds / 100, 0))}</span>
                            </p>
                        </div>
                        : <p className='text-body-2 text-red-600'><strong>Left Group</strong></p>
                    }
                </div>
                {(isOrganizer && slot.userId === group.organizer.id && groupStatus === 'active' && userLimits.addAdditionalFunds) &&
                    (isDemo
                        ? <a className='flex flex-row items-center gap-2 text-blue-500' style={{ cursor: 'pointer' }} onClick={() => toast.info('This action is disabled in the demo')}>
                            <Icons.Plus width="1rem" height="1rem" />
                            <p className='text-body-2'><strong>Add more funds</strong></p>
                        </a>
                        : <Link className='flex flex-row items-center gap-2 text-blue-500' to={`/group/${group.id}/add/${userPledges[0].id}`} onClick={() => toggleBodyScroll(false)}>
                            <Icons.Plus width="1rem" height="1rem" />
                            <p className='text-body-2'><strong>Add more funds</strong></p>
                        </Link>
                    )
                }
            </div>
            <div className='flex flex-col gap-2 items-end flex-1'>
                <CurrencyTextField
                    {...slotAmountProps}
                    invalid={slotsTotal > 0 && slotsTotal !== spendAmount}
                    disabled={submittingLedger || !canSplit || activeTab !== 'Custom' || slotPledgeClosed}
                    onChange={handleUpdate}
                />
                <div style={{ backgroundColor: 'var(--splitcard-gray-100)', padding: '0.125rem 0.5rem', borderRadius: '1rem', width: '100%', textAlign: 'center' }}>
                    <p className='text-body-2' style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--splitcard-grey-500)' }}>{stakePercentage}% stake</p>
                </div>
            </div>
        </div>
    )
}


export default TransactionLedgerEditMerchant
